import { Link } from "react-router-dom";
import PageMeta from "@/components/PageMeta";

export default function Terms() {
  const year = new Date().getFullYear();
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="terms-page">
      <PageMeta
        title="Terms & Conditions"
        description="Recipe Filer's full Terms & Conditions — how your account, your saved family recipes, subscriptions and printed cookbook orders are protected."
        canonical="/terms"
        keywords="Recipe Filer terms and conditions, cookbook app terms of service, recipe app user agreement, family cookbook subscription terms, print on demand cookbook terms, Recipe Filer privacy"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Terms & Conditions",
          url: "https://recipefiler.com/terms",
          description: "Recipe Filer's Terms & Conditions covering accounts, subscriptions, saved recipes and printed cookbook orders.",
          inLanguage: "en-GB",
          isPartOf: { "@type": "WebSite", name: "Recipe Filer", url: "https://recipefiler.com" },
        }}
      />
      <Link to="/" className="text-sm font-bold text-rf-orange hover:underline mb-6 inline-block">← Back home</Link>
      <p className="overline mb-3">Legal</p>
      <h1 className="font-display text-4xl md:text-5xl font-black text-rf-ink mb-3 leading-[0.95]">Terms &amp; Conditions</h1>
      <p className="text-sm text-rf-ink-soft mb-10">Last updated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</p>

      <div className="prose-rf space-y-8 text-rf-ink-soft leading-relaxed">
        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">1. Who we are</h2>
          <p>
            These Terms &amp; Conditions ("<b>Terms</b>") govern your use of
            <b> RecipeFiler.com</b> (the "<b>Service</b>", "<b>we</b>", "<b>our</b>", "<b>us</b>").
            By creating an account, signing in, or otherwise using the Service you agree to these Terms.
            If you do not agree, please do not use the Service.
          </p>
          <p>
            RecipeFiler.com and the Recipe Filer name, logo, page designs, copywriting, and
            illustrations are © {year} RecipeFiler.com. All rights reserved.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">2. Accounts</h2>
          <p>
            You must be at least 13 years old to create an account. You are responsible for keeping your
            sign-in details safe and for any activity under your account. Notify us immediately if you
            suspect unauthorised use of your account.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">3. Your content</h2>
          <p>
            "<b>Your Content</b>" means everything you upload, type, scan, photograph, voice-record, or
            otherwise add to RecipeFiler — recipes, images, audio memories, cookbook covers,
            preferences, and the like.
          </p>
          <p>
            You retain ownership of Your Content. By submitting Your Content to the Service you grant us a
            worldwide, non-exclusive, royalty-free licence to host, store, process, transmit, display, and
            (where you have marked it public) share Your Content for the purpose of providing the
            Service to you and to other users who interact with it.
          </p>
          <p>
            You confirm that Your Content does not infringe anyone else's copyright, trade mark, or
            privacy, and is not unlawful, obscene, defamatory, or otherwise objectionable.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">4. Our content</h2>
          <p>
            All Service content not provided by users — including the RecipeFiler name and logo,
            page layouts, illustrations, AI-generated blog posts, "How To Use" copy, sample recipes,
            and the source code — is © {year} RecipeFiler.com and is protected by copyright and other
            intellectual-property laws. You may not copy, scrape, redistribute, or train a model on
            this content without our prior written permission.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">5. Subscriptions, printing &amp; payments</h2>
          <p>
            Paid plans and physical hardback orders are offered subject to the prices and rules shown
            on the Service at the time of purchase. Where payments are involved we may use third-party
            processors (e.g. Stripe); your card details are handled by those processors, not us.
          </p>
          <p>
            Lifetime memberships gifted by a SuperAdmin or claimed via a valid gift code grant
            unlimited use of paid features at no cost to you, for the life of the Service.
          </p>
          <p>
            Hardback fulfilment is provided by our print partner. Estimated delivery times and shipping
            fees are shown at checkout; final delivery is the print partner's responsibility.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">6. Acceptable use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li>upload Content that is unlawful, hateful, harassing, sexually explicit, or violates anyone's rights;</li>
            <li>reverse-engineer, scrape, or attempt to bypass the security of the Service;</li>
            <li>use the Service to train an AI/ML model without our prior written permission;</li>
            <li>impersonate any person or misrepresent your affiliation with anyone;</li>
            <li>use automated means (bots, scrapers) to access the Service beyond normal browsing.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">7. Termination</h2>
          <p>
            You may delete your account at any time from your Profile page. We may suspend or remove
            accounts that breach these Terms, with or without notice, and may remove Content that
            violates them. Where possible we'll give you a chance to fix the issue first.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">8. AI &amp; OCR features</h2>
          <p>
            The Service uses AI (including third-party services like LlamaParse and large language
            models) to scan, structure, transcribe, and summarise recipes. AI output can contain
            mistakes. <b>Always review AI-extracted recipes before cooking.</b> We are not liable for
            errors in AI-generated ingredient lists, instructions, or substitutions.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">9. Disclaimers &amp; liability</h2>
          <p>
            The Service is provided "as is". To the maximum extent permitted by law, we exclude all
            warranties (express or implied), and our total liability to you for any claim arising from
            or related to the Service is limited to the amount you paid us in the twelve months before
            the claim arose, or £50, whichever is greater.
          </p>
          <p>
            Cooking inherently involves heat, sharp tools, and food allergens. You are responsible for
            cooking safely. We are not a medical or dietary advice service.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">10. Privacy</h2>
          <p>
            We store your account details (email, name, profile picture, subscription tier), the
            recipes and cookbooks you create, and minimal analytics (page views, country) so we can
            run and improve the Service. We do not sell your personal data. Where you sign in with
            Google, we receive only the basics Google sends us (email, name, profile picture).
            Full details are in our <Link to="/privacy" className="text-rf-orange font-bold hover:underline">Privacy Policy</Link>.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">11. Changes</h2>
          <p>
            We may update these Terms from time to time. The "Last updated" date at the top of this
            page reflects the most recent change. Continued use of the Service after a change
            constitutes acceptance of the new Terms.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">12. Governing law</h2>
          <p>
            These Terms are governed by the laws of England &amp; Wales, and disputes will be heard
            in the courts of England &amp; Wales, unless local consumer-protection law gives you a
            stronger right in your own country.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-extrabold text-rf-ink mb-3">13. Contact</h2>
          <p>
            For any question about these Terms, email us at{" "}
            <a href="mailto:contactus@recipefiler.com" className="text-rf-orange font-bold hover:underline">
              contactus@recipefiler.com
            </a>.
          </p>
        </section>

        <p className="text-xs text-rf-ink-soft pt-8 border-t border-rf-ink/10">
          © {year} RecipeFiler.com. All rights reserved. RecipeFiler.com™ is a trade mark of the
          RecipeFiler.com team.
        </p>
      </div>
    </div>
  );
}
